import React, { useEffect, useState } from "react";
import {
  Stack,
  Checkbox,
  Text,
  Card,
  HStack,
  IconButton,
  Spacer,
  Spinner,
  StackSeparator,
} from "@chakra-ui/react";
import { observer } from "mobx-react-lite";
import { t } from "i18next";
import { compressUUID } from "../../../../shared/utils/format-utils";
import { ApiClient, ResourceType } from "../../../models/ApiClient";
import { Survey } from "../../../../survey/models/Survey";
import { AccountApi } from "../../../services/AccountApi";
import { CraWay } from "../../../../cra/models/CraWay";
import {
  ArrowBackIcon,
  ArrowForwardIcon,
} from "../../../../shared/views/icons";
import { SurveyApi } from "../../../../survey/services/SurveyApi";

const i18prefix = "account:Integrations.permissions";

interface Props {
  apiClient: ApiClient;
  permissions: Map<string, ResourceType>;
  setPermissions: React.Dispatch<
    React.SetStateAction<Map<string, ResourceType>>
  >;
}

const ResourceSelectorBase: React.FC<Props> = ({
  apiClient,
  permissions,
  setPermissions,
}) => {
  const [loading, setLoading] = useState(true);
  const [selectedSurvey, setSelectedSurvey] = useState<Survey>();
  const [surveys, setSurveys] = useState<Survey[]>();
  const [craWays, setCraWays] = useState<CraWay[]>();

  useEffect(() => {
    setLoading(true);
    AccountApi.listSurveys(apiClient.accountId)
      .then(({ data: surveys }) => {
        setSurveys(surveys);
      })
      .finally(() => {
        setLoading(false);
      });
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (selectedSurvey) {
      setLoading(true);
      SurveyApi.pageCraWays(selectedSurvey.id, [], 200, 0)
        .then(({ data: page }) => {
          setCraWays(page.data);
        })
        .finally(() => {
          setLoading(false);
        });
    }
    // eslint-disable-next-line
  }, [selectedSurvey]);

  const onChangeItem =
    (resource: ResourceType, entity: string) => (checked: boolean) => {
      setPermissions((prev) => {
        if (checked) {
          prev.set(entity, resource);
        } else {
          prev.delete(entity);
        }
        return new Map(prev);
      });
    };

  return (
    <Card.Root>
      <Stack gap={0} separator={<StackSeparator />}>
        <HStack px={2} py={1}>
          <Text>
            {selectedSurvey
              ? t(`${i18prefix}.selectRoutes`, {
                  survey: selectedSurvey.name,
                })
              : t(`${i18prefix}.selectSurveys`)}
          </Text>
          <Spacer />
          {!!selectedSurvey && (
            <IconButton
              mx={-2}
              my={-1}
              colorPalette="primary"
              size="sm"
              variant="ghost"
              aria-label="Back to survey"
              onClick={() => setSelectedSurvey(undefined)}
            >
              <ArrowBackIcon />
            </IconButton>
          )}
        </HStack>

        <Stack
          gap={0}
          separator={<StackSeparator />}
          height="200px"
          overflowY="auto"
        >
          {loading ? (
            <HStack justifyContent="center" p={4}>
              <Spinner color="primary.600" />
            </HStack>
          ) : selectedSurvey ? (
            craWays?.map((craWay) => (
              <Item
                key={craWay.id}
                id={craWay.id}
                label={craWay.name}
                isChecked={permissions.has(craWay.id)}
                onChange={onChangeItem(ResourceType.CraWay, craWay.id)}
              />
            ))
          ) : (
            surveys?.map((survey) => (
              <Item
                key={survey.id}
                id={survey.id}
                label={`${survey.city}: ${survey.name}`}
                isChecked={permissions.has(survey.id)}
                onChange={onChangeItem(ResourceType.Survey, survey.id)}
                onExpand={() => {
                  if (survey.permissions?.canRead) {
                    setSelectedSurvey(survey);
                  } else {
                    window.alert(
                      t(`${i18prefix}.noAccess`, { survey: survey.name }),
                    );
                  }
                }}
              />
            ))
          )}
        </Stack>
      </Stack>
    </Card.Root>
  );
};

export const ResourceSelector = observer(ResourceSelectorBase);

interface ItemProps {
  id: string;
  label: string;
  isChecked: boolean;
  onChange: (checked: boolean) => void;
  onExpand?: () => void;
}

const Item: React.FC<ItemProps> = ({
  id,
  label,
  isChecked,
  onChange,
  onExpand,
}) => {
  return (
    <HStack px={2} py={1}>
      <Checkbox.Root
        colorPalette="primary"
        size="sm"
        checked={isChecked}
        onCheckedChange={(e: any) => {
          const { checked } = e;
          onChange(checked);
        }}
      >
        <Checkbox.HiddenInput />
        <Checkbox.Control />
        <Checkbox.Label>{label}</Checkbox.Label>
      </Checkbox.Root>

      <Spacer />

      <Text fontFamily="monospace" fontSize="xs">
        {compressUUID(id)}
      </Text>

      {!!onExpand && (
        <IconButton
          mx={-2}
          my={-1}
          colorPalette="primary"
          size="sm"
          variant="ghost"
          aria-label="Select"
          onClick={onExpand}
        >
          <ArrowForwardIcon />
        </IconButton>
      )}
    </HStack>
  );
};
